import React from 'react'
import { Layer, LayerType } from 'modules/shares/types'
import { scaleFrame } from '@sketch/utils'
import { LayerOverlay } from './Layers.styles'

interface LayersTestOverlayProps {
  layers?: Record<string, Layer>
  zoomFactor: number
  layerTypesToSkip: LayerType[]
}

/**
 * This is an overlay containig empty, transparent divs for each layer.
 * The divs are positioned absolutely and have the same size as the layer.
 * This makes our lives easier when testing, because we can just use HTML selectors to find the position of a layer.
 */
const LayersTestOverlayInternal = ({
  layers = {},
  zoomFactor,
  layerTypesToSkip,
}: LayersTestOverlayProps) => (
  <>
    {Object.values(layers)
      // Scale the frame depending on the current zoom
      .map(({ frame, ...rest }) => ({
        frame: scaleFrame(zoomFactor, frame),
        ...rest,
      }))
      // Sort layers depending on the Layer `order` property, have in mind the
      // layers are returned in reverse order, ready to render them, but for
      // any layer calculation within the same level (same parent) a reverse
      // will be needed
      .sort((a, b) => ((a.order as number) || 0) - ((b.order as number) || 0))
      /**
       * We want to avoid rendering some layers:
       * - A layer that is part of a combined shape (shapeGroup)
       * - Some types of layers, defined in SKIP_LAYER_TYPES
       * - Locked layers
       */
      .filter(
        ({ isCombined, type, isLocked }) =>
          !isCombined && !layerTypesToSkip.includes(type) && !isLocked
      )
      .map((layer: Layer) => {
        // Generate an absolute positioned div for each selectable layer
        return (
          <LayerOverlay
            key={layer.id as string}
            data-name={layer.name}
            data-id={layer.id}
            data-testid={layer.id}
            frame={layer.frame}
          />
        )
      })}
  </>
)

export const LayersTestOverlay = React.memo(LayersTestOverlayInternal)
